import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z as zod } from "zod";
import { fetchApiGet, fetchApiPost, fetchApiPut } from "./utils";
import { DeferredPaymentType } from "./deferred_payments";
import { Customer } from "./reports";
import { transformToDecimal } from "./common";

export interface PaginatedResult<T> {
  data: T[],
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  },
}

export interface Activity {
  id: string;
  resource_type: "NOTE" | "TODO";
  content: string;
  status?: "COMPLETED" | "TODO";
  customer_id: string;
  created_at: string;
  updated_at: string;
  created_by_id: string;
  last_updated_by_id: string;

  last_completed_by_id?: string;
  last_assigned_to_id?: string;
  last_assigned_at?: Date;
}

export enum CustomerType {
  Carrier = "carrier",
  Producer = "producer",
  EndUser = "end_user",
  PaymentSplit = "payment_split",
  Client = "client",
}

export const customerTypeOptions = [{
  value: CustomerType.Carrier, label: "Carrier",
}, {
  value: CustomerType.Producer, label: "Producer",
}, {
  value: CustomerType.EndUser, label: "End User",
}, {
  value: CustomerType.PaymentSplit, label: "Payment Split",
}, {
  value: CustomerType.Client, label: "Client",
}];

export const UpdateCustomerDeferredPaymentLienStatusValidation = zod.object({
  customer_id: zod.string().uuid(),
  deferred_payment_lien_status: zod.enum(['active']).optional().nullable(),
});

export const SourcePhoneNumberValidation = zod.object({
  source_type: zod.enum(["destination", "mobile_user"]),
  label: zod.string(),
  phone_number: zod.string().min(1),
});

export const SourceEmailValidation = zod.object({
  source_type: zod.enum(["payment", "payment_ach", "payment_check", "pdf_signature"]),
  label: zod.string(),
  email: zod.string().email(),
});

export const ListCustomerActivities = zod.object({
  customer_type: zod.enum(["risk", "end_user"]).optional().nullable(),
  entity_type: zod.enum(["NOTE", "TODO"]),
  status: zod.enum(["COMPLETED", "TODO"]).optional(),
  customer_id: zod.string().uuid(),
  trader_id: zod.string().uuid().optional(),
  page: zod.coerce.number().min(1).default(1),
  pageSize: zod.coerce.number().min(1).max(50).default(15),
  start_date: zod.string().nullable().optional(),
  end_date: zod.string().nullable().optional(),
  assigned_to_id: zod.string().uuid().optional(),
  created_by_id: zod.string().uuid().optional(),
  updated_by_id: zod.string().uuid().optional(),
  completed_by_id: zod.string().uuid().optional(),
});

export const ListCustomersTodos = zod.object({
  customer_type: zod.enum(["risk", "end_user"]).optional().nullable(),
  entity_type: zod.enum(["NOTE", "TODO"]).optional(),
  customer_id: zod.string().uuid().optional(),
  trader_id: zod.string().uuid().optional(),
  page: zod.coerce.number().min(1).default(1),
  pageSize: zod.coerce.number().min(1).max(50).default(15),
  status: zod.enum(["COMPLETED", "TODO"]).optional(),
  start_date: zod.string().nullable().optional(),
  end_date: zod.string().nullable().optional(),
  assigned_to_id: zod.string().uuid().optional(),
  created_by_id: zod.string().uuid().optional(),
  updated_by_id: zod.string().uuid().optional(),
  completed_by_id: zod.string().uuid().optional(),
});

export const CreateTodoInput = zod.object({
  activity_type: zod.enum(["risk", "sales_notes"]).optional().nullable(),
  resource_type: zod.enum(["TODO"]),
  customer_id: zod.string().uuid(),
  content: zod.string().min(1),
  assigned_to_id: zod.string().uuid().optional().nullable()
});

export const CreateNoteInput = zod.object({
  activity_type: zod.enum(["risk", "sales_notes"]).optional().nullable(),
  resource_type: zod.enum(["NOTE"]),
  customer_id: zod.string().uuid(),
  content: zod.string().min(1),
});

export const UpdateNoteInput = CreateNoteInput.extend({
  id: zod.string().uuid(),
});

export const UpdateTodoInput = CreateTodoInput.extend({
  id: zod.string().uuid(),
  status: zod.enum(["COMPLETED", "TODO"]),
  assigned_to_id: zod.string().uuid().optional().nullable()
});

export const CreateCustomerActivityInput = CreateNoteInput.or(CreateTodoInput);
export const UpdateCustomerActivityInput = UpdateNoteInput.or(UpdateTodoInput);

export const CreateCustomerValidation = zod.object({
  id: zod.string().optional(),

  client_id: zod.string(),
  short_name: zod.string().optional().nullable(),
  long_name: zod.string(),

  is_commercial: zod.boolean(),
  is_cfr: zod.boolean().default(false),

  harvest_iq_profile: zod.string().trim().optional().nullable(),
  skip_harvest_iq_sync: zod.boolean().default(false),

  email: zod.union([zod.string().email(), zod.literal(""), zod.null(), zod.undefined()]).transform(item => {
    if (!item?.trim()) return null;
    return item;
  }),
  address_line_one: zod.string().optional().nullable(),
  address_line_two: zod.string().optional().nullable(),
  city: zod.string().optional().nullable(),
  state: zod.string().optional().nullable(),
  zipcode: zod.string().optional().nullable(),
  country: zod.string().optional().nullable(),
  county: zod.string().optional().nullable(),

  phone_number: zod.string().optional().nullable(),
  source_phone_numbers: zod.array(SourcePhoneNumberValidation).optional(),
  source_emails: zod.array(SourceEmailValidation).optional(),

  preferred_defer_payment_type: zod.enum([DeferredPaymentType.ACH, DeferredPaymentType.CHECK]).optional().nullable(),
  deferred_payment_lien_status: zod.enum(['active']).optional().nullable(),

  gps_latitude: zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_latitude" })),
  gps_longitude: zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_longitude" })),

  marketing_fee: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "marketing_fee" }))),

  customer_type: zod.enum([CustomerType.Carrier, CustomerType.Producer, CustomerType.EndUser, CustomerType.Client, CustomerType.PaymentSplit]),
  active_account_codes: zod.array(zod.string()).default([]),

  trader_id: zod.string().uuid().optional().nullable(),

  partner_names: zod.array(zod.string()).optional().nullable(),
  shirt_sizes: zod.array(zod.string()).optional().nullable(),
  original_acres: zod.number().optional().nullable(),
  bol_sequence: zod.string().optional().nullable(),
  notes: zod.string().optional().nullable(),

  signed_up_date: zod.string().optional().nullable(),

  onboarding_instance_id: zod.string().uuid().optional().nullable(),
});

function nullableUpdatableField(item: any) {
  if (item == null) { return item; }
  switch (typeof item) {
    case "string":
      if (!item?.trim()) return null;
      return item;
    default:
      return item;
  }
}

export const PartialUpdateCustomerValidation = zod.object({
  short_name: zod.string().optional().nullable(),
  long_name: zod.string().optional().nullable(),

  is_commercial: zod.boolean().optional().nullable(),
  is_cfr: zod.boolean().optional().nullable(),

  harvest_iq_profile: zod.preprocess(nullableUpdatableField, zod.string().trim().optional().nullable()),
  skip_harvest_iq_sync: zod.boolean().optional().nullable(),

  email: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  phone_number: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),

  source_phone_numbers: zod.array(SourcePhoneNumberValidation).optional().nullable(),
  source_emails: zod.array(SourceEmailValidation).optional().nullable(),

  address_line_one: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  address_line_two: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  city: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  zipcode: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  state: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  country: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  county: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),

  gps_latitude: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_latitude" }))),
  gps_longitude: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_longitude" }))),

  marketing_fee: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "marketing_fee" }))),

  preferred_defer_payment_type: zod.preprocess(nullableUpdatableField, zod.enum([DeferredPaymentType.ACH, DeferredPaymentType.CHECK]).optional().nullable()),
  deferred_payment_lien_status: zod.preprocess(nullableUpdatableField, zod.enum(["active"]).optional().nullable()),

  customer_type: zod.preprocess(nullableUpdatableField, zod.enum([CustomerType.Carrier, CustomerType.Producer, CustomerType.EndUser, CustomerType.Client, CustomerType.PaymentSplit]).optional().nullable()).optional().nullable(),
  active_account_codes: zod.array(zod.string()).optional().nullable(),

  signed_up_date: zod.string().optional().nullable(),

  trader_id: zod.preprocess(nullableUpdatableField, zod.string().uuid().optional().nullable()),

  notes: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
});

export type BaseCustomer = typeof CreateCustomerValidation._type;

export interface ListCustomerOptions {
  minimal?: boolean;
  hasRiskProfile?: boolean;
  enabled?: boolean;
  fetchInBackground?: boolean;
  customer_type?: "end_user" | null;
}

export const FileUploadValidation = zod.object({
  filename: zod.string(),
  mimetype: zod.string(),
});

export const BulkUploadValidation = zod.object({
  zip_file: FileUploadValidation
});

export function useListClientBrokerAccountNumbers() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["baseCustomersAccountNumbers"],
    queryFn: () => fetchApiGet<{
      unassignedAccountNumberList: string[],
      assignedAccountNumberList: string[],
    }>("customer/broker/account_numbers", queryParams),
    retry: 1,
  });
}

export function useListCustomers(options?: ListCustomerOptions) {
  const queryParams = new URLSearchParams();
  if (options?.minimal) {
    queryParams.set("minimal", "true");
  }
  if (typeof options?.hasRiskProfile === "boolean") {
    queryParams.set("hasRiskProfile", options.hasRiskProfile === true ? "true" : "false");
  }
  if (options?.customer_type) {
    queryParams.set("customer_type", options.customer_type);
  }

  return useQuery({
    queryKey: ["baseCustomers", queryParams.toString()],
    queryFn: () => fetchApiGet<Customer[]>("customer", queryParams),
    retry: 1,
    enabled: options?.enabled ?? true,
    keepPreviousData: options?.fetchInBackground ?? false,
  });
}

export interface AvailableOnboardingInstanceInfo {
  id: string;
  created_at: string;
  template_id: string;
  trigger_meta_data: any;
  template: {
    name: string;
  }
}

export function useListOnboardingCustomerInstances() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["customerOnboardingInstances"],
    queryFn: () => fetchApiGet<AvailableOnboardingInstanceInfo[]>("manage/customer/template/instances/onboarding/available", queryParams),
    retry: 1,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (data: BaseCustomer) => {
    const result = await fetchApiPost<BaseCustomer>(
      "customer",
      data,
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["baseCustomers"] });
      queryClient.invalidateQueries({ queryKey: ["customerOnboardingInstances"] });
    }
    return result;
  };
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (id: string, data: BaseCustomer) => {
    if (data.preferred_defer_payment_type === "" as any) { data.preferred_defer_payment_type = null; }

    const result = await fetchApiPut<BaseCustomer>(
      `customer/${id}`,
      data,
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: ["baseCustomers"] });
    return result;
  };
}

export function useUpdatePartialCustomer() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (id: string, data: Partial<BaseCustomer>) => {

    const result = await fetchApiPut<BaseCustomer>(
      `manage/customer/${id}`,
      data,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["baseCustomers"] });
    }

    return result;
  };
}


export function useUploadBulkPaymentRemittanceJob() {
  const queryParams = new URLSearchParams();

  return async (data: typeof BulkUploadValidation._type) => {
    const result = await fetchApiPost<{ job_id: string; document_id: string; link: string }>(
      `customer/payment/pdf/bulk`,
      data,
      queryParams,
    );

    return result;
  };
}

export function useUpdateCustomerDeferredPaymentLienStatus() {
  const queryParams = new URLSearchParams();
  const queryClient = useQueryClient();

  return async (data: typeof UpdateCustomerDeferredPaymentLienStatusValidation._type) => {
    const result = await fetchApiPut<{ job_id: string; document_id: string; link: string }>(
      `customer/${data.customer_id}/deferred_payment_lien_status`,
      data,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["baseCustomers"] });
    }

    return result;
  };
}

export function useListCustomerActivities(options: zod.infer<typeof ListCustomerActivities>) {
  const queryParams = assembleCustomerActivities(options);

  return useQuery({
    queryKey: ["customerActivities", queryParams.toString()],
    queryFn: () => fetchApiGet<PaginatedResult<any>>(`customer/${options.customer_id}/summary`, queryParams),
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

function assembleCustomerActivities(options: zod.infer<typeof ListCustomerActivities>) {
  const queryParams = new URLSearchParams();
  if (options.customer_type) { queryParams.set("customer_type", options.customer_type); }
  if (options.entity_type) { queryParams.set("entity_type", options.entity_type); }
  if (options.page) { queryParams.set("page", options.page.toString()); }
  if (options.pageSize) { queryParams.set("pageSize", options.pageSize.toString()); }
  if (options.customer_id) { queryParams.set("customer_id", options.customer_id); }
  if (options.trader_id) { queryParams.set("trader_id", options.trader_id); }
  if (options.status) { queryParams.set("status", options.status); }

  if (options.start_date) { queryParams.set("start_date", options.start_date); }
  if (options.end_date) { queryParams.set("end_date", options.end_date); }
  if (options.assigned_to_id) { queryParams.set("assigned_to_id", options.assigned_to_id); }
  if (options.created_by_id) { queryParams.set("created_by_id", options.created_by_id); }
  if (options.updated_by_id) { queryParams.set("updated_by_id", options.updated_by_id); }
  if (options.completed_by_id) { queryParams.set("completed_by_id", options.completed_by_id); }

  return queryParams;
}

export function useListCustomersTodos(options: zod.infer<typeof ListCustomersTodos>) {
  const queryParams = assembleCustomerActivities({ ...options, entity_type: options.entity_type || "TODO" } as any);

  return useQuery({
    queryKey: ["customersActivities", queryParams.toString()],
    queryFn: () => fetchApiGet<PaginatedResult<any>>(`customer/todos`, queryParams),
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

export function useCreateActivity() {
  const queryParams = new URLSearchParams();
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof CreateCustomerActivityInput>) => {
    const result = await fetchApiPost<{}>(
      `customer/${data.customer_id}/activity`,
      data,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["customerActivities"] });
      queryClient.invalidateQueries({ queryKey: ["customersActivities"] });
    }

    return result;
  };
}

export function useUpdateActivity() {
  const queryParams = new URLSearchParams();
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof UpdateCustomerActivityInput>) => {
    const result = await fetchApiPut<{}>(
      `customer/${data.customer_id}/activity`,
      data,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["customerActivities"] });
      queryClient.invalidateQueries({ queryKey: ["customersActivities"] });
    }

    return result;
  };
}
